/**
 * Legacy comments approval queue for brands not managed by Attention OS.
 *
 * Fetches `/api/{brand}/comments/pending` + `/approved` and renders
 * each draft with approve / reject / edit / send actions. Uses the
 * dashboard's existing fetch+toast pattern so styling matches the
 * rest of the views (no new CSS).
 *
 * Attention-managed brands are redirected to the canonical Today queue and
 * the server rejects every legacy mutation. For other brands, every send must
 * route through the approve path first. The /send route refuses
 * status != "approved" with a 409, so the UI's "Send" button is
 * disabled until the operator has approved.
 */

import React, { useEffect, useState, useCallback } from "react";

// Shapes the server returns. Matches engine/comments/types.ts. We
// duplicate the minimum needed for the UI so this view doesn't import
// types from the engine module (avoids vite resolving server-only paths).
interface CommentDraft {
  id: string;
  kind: "reply" | "outbound";
  channel: "x" | "linkedin" | "reddit" | "medium";
  body: string;
  sourceId: string;
  brand: string;
  pieceSlug?: string;
  evalScore: number | null;
  evalViolations: string[];
  healAttempts: number;
  updatedAt: string;
}

interface ApprovalQueueEntry {
  id: string;
  draft: CommentDraft;
  status:
    | "awaiting-eval"
    | "awaiting-approval"
    | "approved"
    | "rejected"
    | "sent"
    | "send-failed";
  reviewerNotes?: string;
  reviewedAt?: string;
  sent?: { permalink: string; sentAt: string };
  lastError?: string;
}

interface CommentsApprovalViewProps {
  brand: string;
  onOpenAttention?: () => void;
}

export default function CommentsApprovalView({ brand, onOpenAttention }: CommentsApprovalViewProps): React.JSX.Element {
  const [pending, setPending] = useState<ApprovalQueueEntry[]>([]);
  const [approved, setApproved] = useState<ApprovalQueueEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState<string>("");
  const [managedByAttention, setManagedByAttention] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        fetch(`/api/${encodeURIComponent(brand)}/comments/pending`),
        fetch(`/api/${encodeURIComponent(brand)}/comments/approved`),
      ]);
      if (!pendingRes.ok || !approvedRes.ok) {
        throw new Error(`HTTP ${pendingRes.status} / ${approvedRes.status}`);
      }
      const pendingJson = (await pendingRes.json()) as { entries?: ApprovalQueueEntry[]; managedBy?: string };
      const approvedJson = (await approvedRes.json()) as { entries?: ApprovalQueueEntry[]; managedBy?: string };
      setManagedByAttention(pendingJson.managedBy === "attention-os" || approvedJson.managedBy === "attention-os");
      setPending(pendingJson.entries ?? []);
      setApproved(approvedJson.entries ?? []);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [brand]);

  useEffect(() => {
    void refresh();
    // No polling — the dashboard's existing SSE stream doesn't cover
    // comment events. Operator hits Refresh after each action.
  }, [refresh]);

  const post = useCallback(
    async (id: string, action: "approve" | "reject" | "send", body?: Record<string, unknown>): Promise<void> => {
      setBusyId(id);
      try {
        const res = await fetch(
          `/api/${encodeURIComponent(brand)}/comments/${encodeURIComponent(id)}/${action}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: body ? JSON.stringify(body) : undefined,
          },
        );
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(errBody.error ?? `HTTP ${res.status}`);
        }
        await refresh();
      } catch (err) {
        setError(`${action} failed: ${(err as Error).message}`);
      } finally {
        setBusyId(null);
      }
    },
    [brand, refresh],
  );

  const saveEdit = useCallback(
    async (id: string): Promise<void> => {
      if (!editBody.trim()) return;
      setBusyId(id);
      try {
        const res = await fetch(
          `/api/${encodeURIComponent(brand)}/comments/${encodeURIComponent(id)}/edit`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ body: editBody.trim() }),
          },
        );
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(errBody.error ?? `HTTP ${res.status}`);
        }
        setEditId(null);
        setEditBody("");
        await refresh();
      } catch (err) {
        setError(`edit failed: ${(err as Error).message}`);
      } finally {
        setBusyId(null);
      }
    },
    [brand, editBody, refresh],
  );

  if (managedByAttention) {
    return (
      <div className="view-page">
        <h2 className="view-page__title">Conversations moved to Attention</h2>
        <p style={{ maxWidth: 720, color: "var(--text-secondary)" }}>
          This brand has one conversation queue and one approval history. Discover, draft, and decide in Attention → Today; send the public reply or comment manually in the named account, then record its permalink.
        </p>
        {onOpenAttention && <button onClick={onOpenAttention}>Open Attention Today</button>}
      </div>
    );
  }

  const renderEntry = (entry: ApprovalQueueEntry, includeSend: boolean): React.JSX.Element => {
    const isEditing = editId === entry.id;
    const isBusy = busyId === entry.id;
    return (
      <div
        key={entry.id}
        style={{
          padding: 16,
          marginBottom: 16,
          background: "var(--bg)",
          border: "1px solid var(--border)",
          borderRadius: 0,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)" }}>
            <strong>{entry.draft.kind}</strong> · {entry.draft.channel} · score={entry.draft.evalScore ?? "—"} · {entry.id.slice(0, 24)}…
          </div>
          <div style={{ fontSize: "var(--fs-meta)", color: "var(--text-secondary)" }}>{entry.status}</div>
        </div>
        {isEditing ? (
          <div>
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              rows={4}
              style={{
                width: "100%",
                padding: 8,
                marginBottom: 8,
                fontFamily: "var(--font-mono, monospace)",
                fontSize: "var(--fs-small)",
                background: "var(--bg-secondary)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: 0,
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { void saveEdit(entry.id); }} disabled={isBusy}>
                Save edit
              </button>
              <button onClick={() => { setEditId(null); setEditBody(""); }} disabled={isBusy}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: 12,
              marginBottom: 12,
              background: "var(--bg-secondary)",
              borderRadius: 0,
              fontFamily: "var(--font-mono, monospace)",
              fontSize: "var(--fs-body)",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {entry.draft.body}
          </div>
        )}
        {entry.draft.evalViolations.length > 0 && !isEditing && (
          <div style={{ marginBottom: 12, fontSize: "var(--fs-meta)", color: "var(--warning)" }}>
            <strong>Violations:</strong>
            <ul style={{ margin: "4px 0 0 16px" }}>
              {entry.draft.evalViolations.map((v, i) => <li key={i}>{v}</li>)}
            </ul>
          </div>
        )}
        {!isEditing && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => { void post(entry.id, "approve"); }}
              disabled={isBusy || entry.status === "approved"}
            >
              Approve
            </button>
            <button
              onClick={() => { void post(entry.id, "reject"); }}
              disabled={isBusy || entry.status === "rejected"}
            >
              Reject
            </button>
            <button
              onClick={() => { setEditId(entry.id); setEditBody(entry.draft.body); }}
              disabled={isBusy || entry.status === "sent"}
            >
              Edit
            </button>
            {includeSend && (
              <button
                onClick={() => { void post(entry.id, "send"); }}
                disabled={isBusy || entry.status !== "approved"}
                title={entry.status !== "approved" ? "Approve first" : "Send to channel"}
              >
                Send
              </button>
            )}
          </div>
        )}
        {entry.lastError && (
          <div style={{ marginTop: 12, fontSize: "var(--fs-meta)", color: "var(--danger)" }}>
            <strong>Last error:</strong> {entry.lastError}
          </div>
        )}
        {entry.sent && (
          <div style={{ marginTop: 12, fontSize: "var(--fs-meta)" }}>
            Sent at <code>{entry.sent.sentAt}</code> → <a href={entry.sent.permalink} target="_blank" rel="noreferrer">{entry.sent.permalink}</a>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="view-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 24 }}>
        <h2 className="view-page__title">Comments approval</h2>
        <button onClick={() => { void refresh(); }}>Refresh</button>
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: "var(--danger-light)",
            color: "var(--danger)",
            borderRadius: 0,
          }}
        >
          {error}
        </div>
      )}

      <h3 className="view-section-title">Awaiting approval ({pending.length})</h3>
      {pending.length === 0 ? (
        <div style={{ color: "var(--text-secondary)", marginBottom: 24 }}>
          No comment drafts awaiting approval. Run{" "}
          <code>bun run orchestrate.ts draft-comment {brand} reply &lt;inbound-id&gt;</code>{" "}
          to create one.
        </div>
      ) : (
        pending.map((e) => renderEntry(e, false))
      )}

      <h3 className="view-section-title" style={{ marginTop: 32 }}>
        Approved — ready to send ({approved.length})
      </h3>
      {approved.length === 0 ? (
        <div style={{ color: "var(--text-secondary)" }}>
          Nothing approved-but-not-sent. Approve a draft above to populate this list.
        </div>
      ) : (
        approved.map((e) => renderEntry(e, true))
      )}
    </div>
  );
}
